/**
 * Retention — week-over-week line + weekly and monthly cohort grids.
 * See docs/FeatureDesigns/teacher-stats.md §3.3.
 */

import { useState, useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import type { TeacherCohortRow, TeacherStatsResponse } from '../../api/adminApi';
import { CohortGrid } from '../dashboard/RetentionTab';

interface Props {
    stats: TeacherStatsResponse;
}

function formatWeek(date: string): string {
    const d = new Date(date + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatWeekRange(date: string): string {
    const start = new Date(date + 'T00:00:00Z');
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    const startLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    const endLabel = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return `${startLabel} – ${endLabel}`;
}

const TOOLTIP_CONTENT_STYLE = {
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
};
const TOOLTIP_LABEL_STYLE = { color: 'var(--text-primary)' };
const TOOLTIP_ITEM_STYLE = { color: 'var(--text-primary)' };

export function RetentionSection({ stats }: Props) {
    const wow = useMemo(() => stats.weeklyRetention.map((w, i) => {
        const prev = i > 0 ? stats.weeklyRetention[i - 1] : null;
        return {
            weekStart: w.weekStart,
            any: prev && prev.activeAny > 0 ? Math.round(100 * w.retainedAny / prev.activeAny) : null,
            attempted: prev && prev.activeAttempted > 0 ? Math.round(100 * w.retainedAttempted / prev.activeAttempted) : null,
        };
    }), [stats.weeklyRetention]);

    return (
        <section className="ts-section">
            <h3>Retention</h3>
            <p className="ts-section-sub">
                Week-over-week percentage of last week's actives who returned this week, over the last 8 weeks. The cohort grids below group students by the week and the month they enrolled.
            </p>

            <div className="ts-chart-card">
                <h4>Week-over-week retention (%)</h4>
                <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={wow} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                        <XAxis
                            dataKey="weekStart"
                            tickFormatter={formatWeek}
                            tick={{ fontSize: 11 }}
                            stroke="#9ca3af"
                        />
                        <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 11 }}
                            stroke="#9ca3af"
                            unit="%"
                        />
                        <Tooltip
                            labelFormatter={(label) => formatWeekRange(String(label))}
                            formatter={(value, name) => [value === null ? '—' : `${value}%`, name]}
                            contentStyle={TOOLTIP_CONTENT_STYLE}
                            labelStyle={TOOLTIP_LABEL_STYLE}
                            itemStyle={TOOLTIP_ITEM_STYLE}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="any" name="Any activity" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        <Line type="monotone" dataKey="attempted" name="Attempted" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <CohortTables stats={stats} />
        </section>
    );
}

/**
 * The 12-week and 12-month cohort grids — the main Retention tab's grid and
 * spans, so the two read the same way. Per teacher a cohort is the students
 * who enrolled with them that period; in the all-teachers view, who signed up.
 */
function CohortTables({ stats }: Props) {
    const [metric, setMetric] = useState<'any' | 'attempted'>('any');
    const bySignup = stats.cohortAnchor === 'signup';
    const anchor = bySignup ? 'Signup' : 'Enrollment';
    const who = bySignup ? 'signed up to the platform' : 'enrolled with this teacher';
    const verb = metric === 'any' ? 'active at all' : 'attempted a question';

    const toggle = (
        <div className="ts-cohort-controls">
            <span className="ts-section-sub" style={{ margin: 0 }}>Metric:</span>
            <div className="ts-range-toggle">
                <button className={metric === 'any' ? 'active' : ''} onClick={() => setMetric('any')}>Any activity</button>
                <button className={metric === 'attempted' ? 'active' : ''} onClick={() => setMetric('attempted')}>Attempted</button>
            </div>
        </div>
    );
    const rows = (cohorts: TeacherCohortRow[]) => cohorts.map(c => ({
        start: c.start,
        size: c.size,
        active: metric === 'any' ? c.activeAny : c.activeAttempted,
    }));
    const note = (unit: string, units: string, prefix: string, span: string) => (
        <>
            The {span}, whatever the range above. Each row is the students who {who} that {unit};{' '}
            {prefix}<em>n</em> is how many of them were {verb} n {units} later ({prefix}0 = that {unit}).
        </>
    );

    return (
        <>
            <CohortGrid
                grain="week"
                title="Weekly cohort retention"
                anchorLabel={`${anchor} week`}
                rows={rows(stats.cohortWeekly)}
                note={note('week', 'weeks', 'W', 'last 12 weeks')}
                controls={toggle}
            />
            <CohortGrid
                grain="month"
                title="Monthly cohort retention"
                anchorLabel={`${anchor} month`}
                rows={rows(stats.cohortMonthly)}
                note={note('month', 'months', 'M', 'last 12 months')}
                controls={toggle}
            />
        </>
    );
}
